import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import { Link2, Plus, Trash2, ChevronLeft, User, Building2, Pencil, Check, X, Search, ChevronDown } from 'lucide-react';
import AddCompanyModal from '../../crm/components/AddCompanyModal';

// Badge config per company role in the chain
const getBadgeStyle = (link, isEndNode) => {
  if (isEndNode === 'prophecy') {
    return {
      pill:  { background: '#3b5bfd', color: '#fff', fontWeight: 700 },
      badge: { background: '#eff3ff', color: '#3b5bfd' },
      label: 'You'
    };
  }
  if (link.IsEndClient) {
    return {
      pill:  { background: '#fef3c7', color: '#92400e', fontWeight: 600 },
      badge: { background: '#fde68a', color: '#78350f' },
      label: 'End Client \u2605'
    };
  }
  if (link.IsMSP || link.RoleLabel?.toUpperCase() === 'MSP') {
    return {
      pill:  { background: '#ffedd5', color: '#9a3412', fontWeight: 600 },
      badge: { background: '#fed7aa', color: '#7c2d12' },
      label: 'MSP'
    };
  }
  if (link.IsW2Employer) {
    return {
      pill:  { background: '#dbeafe', color: '#1e40af', fontWeight: 600 },
      badge: { background: '#bfdbfe', color: '#1e3a8a' },
      label: 'W2 Employer'
    };
  }
  if (link.ChainSide === 'CLIENT') {
    return {
      pill:  { background: '#f3f4f6', color: '#374151', fontWeight: 500 },
      badge: { background: '#e5e7eb', color: '#4b5563' },
      label: 'Intermediary'
    };
  }
  return {
    pill:  { background: '#ede9fe', color: '#5b21b6', fontWeight: 500 },
    badge: { background: '#ddd6fe', color: '#4c1d95' },
    label: 'Supplier'
  };
};

const STAGE_META = {
  job:        { label: 'Draft Chain',                  icon: '\uD83D\uDCCB', color: '#6b7280' },
  submission:  { label: 'Working Chain',                icon: '\uD83D\uDD04', color: '#2563eb' },
  deployment:  { label: 'Final Chain (Authoritative)', icon: '\u2705',       color: '#16a34a' }
};

const PlacementChainDisplay = ({ 
  jobId, 
  entityType = 'job', 
  isEditMode = false, 
  clientName = '', 
  vendorName = '', 
  candidateName = '',
  showSupplierCandidate,
  relatedJobId = null,
  hasApplications = false,
  onSyncApplications = null,
  onChainSaved = null,
  onChainChange = null,
  initialChain = null
}) => {
  const [chain, setChain]                 = useState(Array.isArray(initialChain) ? initialChain : []);
  const [companies, setCompanies]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [isAdding, setIsAdding]           = useState(false);
  const [editingLinkId, setEditingLinkId]       = useState(null); // null = adding, linkId = editing
  const [editingCompanyName, setEditingCompanyName] = useState(''); // display name of the link being edited
  const [isEditingChain, setIsEditingChain] = useState(false);
  const [isSubmittingLink, setIsSubmittingLink] = useState(false);
  const [isSavingChain, setIsSavingChain] = useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [isSyncing, setIsSyncing]         = useState(false);
  const [hasChainChanges, setHasChainChanges] = useState(false);
  const [syncFeedback, setSyncFeedback]   = useState('');
  const [hiddenVirtuals, setHiddenVirtuals] = useState([]);
  const [formError, setFormError]         = useState('');
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const companyDropdownRef = useRef(null);
  const [formData, setFormData]           = useState({
    companyId: '', chainSide: 'CLIENT', sequenceNo: 1,
    isEndClient: false, isW2Employer: false, roleLabel: ''
  });
  const [mspPickerData, setMspPickerData] = useState(null);

  // Close company dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target)) {
        setShowCompanyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCompanies = useMemo(() => {
    if (!companySearchTerm.trim()) return companies;
    const term = companySearchTerm.toLowerCase().trim();
    return companies.filter(c => {
      const disp = (c.DisplayName || '').toLowerCase();
      const comp = (c.CompanyName || '').toLowerCase();
      const code = (c.CompanyCode || '').toLowerCase();
      return disp.includes(term) || comp.includes(term) || code.includes(term);
    });
  }, [companies, companySearchTerm]);

  const selectedCompany = useMemo(() => {
    return companies.find(c => String(c.CompanyId) === String(formData.companyId));
  }, [companies, formData.companyId]);


  const activeEdit = isEditMode || isEditingChain;

  const pkName = entityType.charAt(0).toUpperCase() + entityType.slice(1) + 'ChainId';

  // Determine whether supplier side companies & Candidate node should be displayed
  const isSupplierVisible = showSupplierCandidate !== undefined 
    ? showSupplierCandidate 
    : (entityType.toLowerCase() === 'submission' || entityType.toLowerCase() === 'deployment');

  const fetchChain = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const queryUrl = relatedJobId 
        ? `${BASE_URL}/api/crm/chains/${entityType}/${jobId}?jobId=${relatedJobId}`
        : `${BASE_URL}/api/crm/chains/${entityType}/${jobId}`;

      const res = await axios.get(queryUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        const fetchedData = res.data.data || [];
        setChain(fetchedData);
        const maxSeq = fetchedData.reduce((m, x) => Math.max(m, x.SequenceNo), 0);
        setFormData(prev => ({ ...prev, sequenceNo: maxSeq + 1 }));
        return fetchedData;
      }
      return [];
    } catch (err) {
      console.error('Error fetching chain:', err);
      // Fallback: If initialChain provided and valid, keep it rather than emptying out
      if (Array.isArray(initialChain) && initialChain.length > 0) {
        setChain(prev => (prev && prev.length > 0 ? prev : initialChain));
        return initialChain;
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, [jobId, entityType, relatedJobId, initialChain]);

  // Sync initialChain if passed and chain is currently empty
  useEffect(() => {
    if (Array.isArray(initialChain) && initialChain.length > 0 && !isEditingChain && !isAdding) {
      setChain(prev => {
        if (!prev || prev.length === 0) return initialChain;
        return prev;
      });
    }
  }, [initialChain, isEditingChain, isAdding]);

  const fetchCompanies = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/crm/companies?status=Active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) setCompanies(res.data.data);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  }, []);

  useEffect(() => {
    fetchChain();
    if (activeEdit) fetchCompanies();
  }, [fetchChain, fetchCompanies, activeEdit]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!editingLinkId && !formData.companyId) {
      setFormError('Please select a company.');
      return;
    }
    setIsSubmittingLink(true);
    try {
      const token = localStorage.getItem('token');

      if (editingLinkId) {
        // ── UPDATE existing link ──
        const res = await axios.put(`${BASE_URL}/api/crm/chains/${entityType}/${jobId}/${editingLinkId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          setIsAdding(false);
          setEditingLinkId(null);
          setFormData({ companyId: '', chainSide: 'CLIENT', sequenceNo: 1, isEndClient: false, isW2Employer: false, roleLabel: '' });
          if (entityType.toLowerCase() === 'job') setHasChainChanges(true);
          const updated = await fetchChain();
          if (onChainSaved) onChainSaved(updated);
          if (onChainChange) onChainChange(updated);
        } else {
          setFormError(res.data.message || 'Failed to update chain link.');
        }
      } else {
        // ── ADD new link ──
        // Check if selected company is on CLIENT side and has active MSP relationships
        if (formData.chainSide === 'CLIENT') {
          try {
            const mspCheckRes = await axios.get(`${BASE_URL}/api/crm/companies/${formData.companyId}/msp-check`, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (mspCheckRes.data.success && mspCheckRes.data.hasMSP && mspCheckRes.data.count > 0) {
              const msps = mspCheckRes.data.msps || [];
              if (msps.length === 1) {
                // Exactly 1 MSP: Client first at sequenceNo, then MSP after Client (closest to Prophecy)
                const msp = msps[0];
                const clientSeq = formData.sequenceNo || 1;

                await axios.post(`${BASE_URL}/api/crm/chains/${entityType}/${jobId}`, {
                  ...formData,
                  sequenceNo: clientSeq
                }, { headers: { Authorization: `Bearer ${token}` } });

                const mspAlreadyInChain = chain.some(l => String(l.CompanyId) === String(msp.MSPCompanyId));
                if (!mspAlreadyInChain) {
                  await axios.post(`${BASE_URL}/api/crm/chains/${entityType}/${jobId}`, {
                    companyId: msp.MSPCompanyId,
                    chainSide: 'CLIENT',
                    sequenceNo: clientSeq + 1,
                    isEndClient: false,
                    isW2Employer: false,
                    roleLabel: 'MSP'
                  }, { headers: { Authorization: `Bearer ${token}` } });
                }

                setIsAdding(false);
                setFormData({ companyId: '', chainSide: 'CLIENT', sequenceNo: 1, isEndClient: false, isW2Employer: false, roleLabel: '' });
                if (entityType.toLowerCase() === 'job') setHasChainChanges(true);
                const updated = await fetchChain();
                if (onChainSaved) onChainSaved(updated);
                if (onChainChange) onChainChange(updated);
                return;
              } else if (msps.length > 1) {
                // 2+ MSPs: Prompt recruiter with MSP Picker modal
                setMspPickerData({
                  clientCompanyId: formData.companyId,
                  clientName: selectedCompany?.DisplayName || selectedCompany?.CompanyName || 'Client',
                  msps: msps,
                  clientFormData: { ...formData }
                });
                return;
              }
            }
          } catch (checkErr) {
            console.warn('MSP check failed, proceeding normally:', checkErr);
          }
        }

        const res = await axios.post(`${BASE_URL}/api/crm/chains/${entityType}/${jobId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          setIsAdding(false);
          setFormData({ companyId: '', chainSide: 'CLIENT', sequenceNo: 1, isEndClient: false, isW2Employer: false, roleLabel: '' });
          if (entityType.toLowerCase() === 'job') setHasChainChanges(true);
          const updated = await fetchChain();
          if (onChainSaved) onChainSaved(updated);
          if (onChainChange) onChainChange(updated);
        } else {
          setFormError(res.data.message || 'Failed to add chain link.');
        }
      }
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save chain link.');
    } finally {
      setIsSubmittingLink(false);
    }
  };

  const handleSelectMspForChain = async (selectedMspId) => {
    if (!mspPickerData) return;
    setIsSubmittingLink(true);
    try {
      const token = localStorage.getItem('token');
      const clientSeq = mspPickerData.clientFormData.sequenceNo || 1;

      // Add Client first
      await axios.post(`${BASE_URL}/api/crm/chains/${entityType}/${jobId}`, {
        ...mspPickerData.clientFormData,
        sequenceNo: clientSeq
      }, { headers: { Authorization: `Bearer ${token}` } });

      // Add chosen MSP after Client (closest to Prophecy)
      await axios.post(`${BASE_URL}/api/crm/chains/${entityType}/${jobId}`, {
        companyId: selectedMspId,
        chainSide: 'CLIENT',
        sequenceNo: clientSeq + 1,
        isEndClient: false,
        isW2Employer: false,
        roleLabel: 'MSP'
      }, { headers: { Authorization: `Bearer ${token}` } });

      setMspPickerData(null);
      setIsAdding(false);
      setFormData({ companyId: '', chainSide: 'CLIENT', sequenceNo: 1, isEndClient: false, isW2Employer: false, roleLabel: '' });
      if (entityType.toLowerCase() === 'job') setHasChainChanges(true);
      const updated = await fetchChain();
      if (onChainSaved) onChainSaved(updated);
      if (onChainChange) onChainChange(updated);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to auto-insert MSP and Client.');
    } finally {
      setIsSubmittingLink(false);
    }
  };

  const handleSkipMspForChain = async () => {
    if (!mspPickerData) return;
    setIsSubmittingLink(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BASE_URL}/api/crm/chains/${entityType}/${jobId}`, mspPickerData.clientFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMspPickerData(null);
      setIsAdding(false);
      setFormData({ companyId: '', chainSide: 'CLIENT', sequenceNo: 1, isEndClient: false, isW2Employer: false, roleLabel: '' });
      if (entityType.toLowerCase() === 'job') setHasChainChanges(true);
      const updated = await fetchChain();
      if (onChainSaved) onChainSaved(updated);
      if (onChainChange) onChainChange(updated);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add client to chain.');
    } finally {
      setIsSubmittingLink(false);
    }
  };

  const handleEdit = (node) => {
    // Pre-fill form with this node's existing data
    setEditingLinkId(node[pkName]);
    setEditingCompanyName(node.DisplayName || node.CompanyName || '');
    setFormData({
      companyId:   node.CompanyId   || '',
      chainSide:   node.ChainSide   || 'CLIENT',
      sequenceNo:  node.SequenceNo  || 1,
      isEndClient: node.IsEndClient || false,
      isW2Employer:node.IsW2Employer|| false,
      roleLabel:   node.RoleLabel   || ''
    });
    setFormError('');
    setIsAdding(true);
    // No need to fetch companies list when editing — company cannot be changed
  };

  const handleDelete = async (node) => {
    if (node && typeof node === 'object' && node.IsVirtual) {
      if (window.confirm(`Remove ${node.DisplayName || node.CompanyName} from the chain?`)) {
        setHiddenVirtuals(prev => [...prev, node[pkName]]);
      }
      return;
    }

    const linkId = (node && typeof node === 'object') ? node[pkName] : node;
    if (!window.confirm('Remove this company from the chain?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/api/crm/chains/${entityType}/${jobId}/${linkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (entityType.toLowerCase() === 'job') setHasChainChanges(true);
      const updated = await fetchChain();
      if (onChainSaved) onChainSaved(updated);
      if (onChainChange) onChainChange(updated);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to remove chain link.');
    }
  };

  const handleCompanyCreated = async () => {
    setShowAddCompanyModal(false);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/crm/companies?status=Active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setCompanies(res.data.data);
        const newestCompany = res.data.data[0];
        if (newestCompany) {
          setFormData(prev => ({ ...prev, companyId: newestCompany.CompanyId }));
        }
      }
    } catch (err) {
      console.error('Error refreshing companies after creation:', err);
    }
  };

  // Helper to sort client side: End Client (IsEndClient = true) ALWAYS comes first (furthest left), then direct/intermediary client, then MSP (closest to Prophecy), then SequenceNo ascending
  const sortClientSide = (list) => {
    return [...list].sort((a, b) => {
      const aEnd = Boolean(a.IsEndClient);
      const bEnd = Boolean(b.IsEndClient);
      if (aEnd && !bEnd) return -1;
      if (!aEnd && bEnd) return 1;

      // MSP nodes sit closest to Prophecy on the client side (after intermediary / direct client)
      const aIsMsp = Boolean(a.IsMSP || (a.RoleLabel && a.RoleLabel.toUpperCase() === 'MSP'));
      const bIsMsp = Boolean(b.IsMSP || (b.RoleLabel && b.RoleLabel.toUpperCase() === 'MSP'));
      if (!aIsMsp && bIsMsp) return -1;
      if (aIsMsp && !bIsMsp) return 1;

      return (a.SequenceNo || 0) - (b.SequenceNo || 0);
    });
  };

  // Build ordered pipeline: CLIENT side (End Client furthest left) | PROPHECY | SUPPLIER asc (if visible)
  let clientSide   = sortClientSide(chain.filter(l => l.ChainSide === 'CLIENT'));
  let supplierSide = chain.filter(l => l.ChainSide === 'SUPPLIER').sort((a, b) => a.SequenceNo - b.SequenceNo);

  // Keep role's clientName (e.g. TCS) in clientSide unless already added as a DB link or hidden by user
  if (clientName && clientName.trim() !== '' && !hiddenVirtuals.includes('virtual_client')) {
    const trimmedClient = clientName.trim().toLowerCase();
    const alreadyInChain = clientSide.some(l => {
      const disp = (l.DisplayName || '').trim().toLowerCase();
      const comp = (l.CompanyName || '').trim().toLowerCase();
      const dispBase = disp.replace(/\s*\((in|us|usa|india)\)/gi, '').trim();
      const compBase = comp.replace(/\s*\((in|us|usa|india)\)/gi, '').trim();
      return disp === trimmedClient || 
             comp === trimmedClient || 
             disp.startsWith(trimmedClient) || 
             comp.startsWith(trimmedClient) ||
             trimmedClient.startsWith(disp) ||
             (dispBase.length >= 3 && trimmedClient.includes(dispBase)) ||
             (compBase.length >= 3 && trimmedClient.includes(compBase));
    });
    if (!alreadyInChain) {
      const hasEndClient = clientSide.some(l => l.IsEndClient);
      const minClientSeq = clientSide.reduce((m, x) => Math.min(m, x.SequenceNo || 1), 999);
      const maxClientSeq = clientSide.reduce((m, x) => Math.max(m, x.SequenceNo || 0), 0);
      
      let seq = 1;
      if (hasEndClient || minClientSeq === 1) {
        // If a DB client is End Client or occupies Seq 1, virtual client goes after it
        seq = Math.max(2, maxClientSeq + 1);
      } else if (clientSide.length > 0) {
        // DB clients occupy Seq 2, 3..., so virtual client stays at Seq 1 (before them)
        seq = 1;
      }

      clientSide.push({
        [pkName]: 'virtual_client',
        CompanyName: clientName.trim(),
        DisplayName: clientName.trim(),
        ChainSide: 'CLIENT',
        SequenceNo: seq,
        IsVirtual: true
      });
      clientSide = sortClientSide(clientSide);
    }
  }

  // Keep role's vendorName in supplierSide unless already added as a DB link or hidden by user (only if supplier side visible)
  if (isSupplierVisible && vendorName && vendorName.trim() !== '' && !hiddenVirtuals.includes('virtual_vendor')) {
    const trimmedVendor = vendorName.trim().toLowerCase();
    const alreadyInChain = supplierSide.some(l => {
      const disp = (l.DisplayName || '').trim().toLowerCase();
      const comp = (l.CompanyName || '').trim().toLowerCase();
      return disp === trimmedVendor || 
             comp === trimmedVendor || 
             disp.startsWith(trimmedVendor) || 
             comp.startsWith(trimmedVendor) ||
             trimmedVendor.startsWith(disp);
    });
    if (!alreadyInChain) {
      supplierSide.push({
        [pkName]: 'virtual_vendor',
        CompanyName: vendorName.trim(),
        DisplayName: vendorName.trim(),
        ChainSide: 'SUPPLIER',
        SequenceNo: 1,
        IsVirtual: true
      });
      supplierSide.sort((a, b) => a.SequenceNo - b.SequenceNo);
    }
  }

  // Right side (vendors & candidate): Included ONLY if isSupplierVisible is true
  const hasSuppliers = supplierSide.length > 0;
  const rightSide = isSupplierVisible ? (hasSuppliers ? [...supplierSide, 'CANDIDATE'] : ['CANDIDATE']) : [];

  const pipeline = [...clientSide, 'PROPHECY', ...rightSide];

  const handleGapClick = (leftNode, rightNode) => {
    let chainSide = 'CLIENT';
    let sequenceNo = 1;

    const isSupplierSide = (leftNode === 'PROPHECY') ||
                           (leftNode && typeof leftNode === 'object' && leftNode.ChainSide === 'SUPPLIER') ||
                           (rightNode === 'CANDIDATE');

    if (isSupplierSide) {
      chainSide = 'SUPPLIER';
      if (leftNode === 'PROPHECY') {
        sequenceNo = 1;
      } else if (leftNode && typeof leftNode === 'object' && leftNode.SequenceNo) {
        sequenceNo = leftNode.SequenceNo + 1;
      } else {
        sequenceNo = 1;
      }
    } else {
      chainSide = 'CLIENT';
      if (rightNode === 'PROPHECY') {
        // Gap is directly before PROPHECY (after all existing client nodes)
        const maxSeq = clientSide.reduce((m, x) => Math.max(m, x.SequenceNo || 0), 0);
        sequenceNo = maxSeq + 1;
      } else if (leftNode && typeof leftNode === 'object' && leftNode.SequenceNo) {
        // Gap is after a client node (e.g., between Client A and Client B)
        sequenceNo = leftNode.SequenceNo + 1;
      } else if (rightNode && typeof rightNode === 'object' && rightNode.SequenceNo) {
        // Gap is before a client node (e.g., at the far left before the first client node)
        sequenceNo = rightNode.SequenceNo;
      } else {
        const maxSeq = clientSide.reduce((m, x) => Math.max(m, x.SequenceNo || 0), 0);
        sequenceNo = maxSeq + 1;
      }
    }

    const isAtEndClientPosition = !isSupplierSide && leftNode === null;

    setFormData({
      companyId: '',
      chainSide,
      sequenceNo,
      isEndClient: isAtEndClientPosition,
      isW2Employer: false,
      roleLabel: ''
    });
    setFormError('');
    setIsAdding(true);
    fetchCompanies();
  };

  const renderNode = (node) => {
    if (node === 'CANDIDATE') {
      const displayName = candidateName && candidateName.trim() !== '' ? candidateName.trim() : 'Candidate';
      return (
        <div key="candidate" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
          <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#ede9fe',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <User size={15} color="#7c3aed" />
          </div>
          <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500, maxWidth: '90px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={displayName}>
            {displayName}
          </span>
        </div>
      );
    }
    if (node === 'PROPHECY') {
      const s = getBadgeStyle(null, 'prophecy');
      return (
        <div key="prophecy" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
          <div style={{ padding: '4px 12px', borderRadius: '14px', fontSize: '11px', ...s.pill }}>PROPHECY</div>
          <span style={{ fontSize: '9px', padding: '1px 6px', borderRadius: '8px', ...s.badge }}>{s.label}</span>
        </div>
      );
    }
    const s    = getBadgeStyle(node);
    const name = node.DisplayName || node.CompanyName;
    return (
      <div key={node[pkName]} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
        <div style={{ padding: '4px 10px', borderRadius: '14px', fontSize: '11px', ...s.pill,
                      display: 'flex', alignItems: 'center', gap: '4px' }}>
          {name}
          {activeEdit && (
            <>
              <button onClick={() => handleEdit(node)} title="Edit this link"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                         color: '#2563eb', display: 'flex', alignItems: 'center', marginLeft: '2px' }}>
                <Pencil size={10} />
              </button>
              <button onClick={() => handleDelete(node)} title="Remove from chain"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                         color: '#ef4444', display: 'flex', alignItems: 'center', marginLeft: '2px' }}>
                <Trash2 size={10} />
              </button>
            </>
          )}
        </div>
        <span style={{ fontSize: '9px', padding: '1px 6px', borderRadius: '8px', ...s.badge }}>{s.label}</span>
        {node.RoleLabel && (
          <span style={{ fontSize: '9px', color: '#9ca3af', marginTop: '1px' }}>{node.RoleLabel}</span>
        )}
      </div>
    );
  };

  const GapButton = ({ leftNode, rightNode }) => {
    // When supplier side is hidden (Recruitment view), suppress the (+) after PROPHECY
    // so users cannot accidentally add supplier nodes
    const isAfterProphecy = leftNode === 'PROPHECY';
    const isSupplierGap = isAfterProphecy ||
      (leftNode && typeof leftNode === 'object' && leftNode.ChainSide === 'SUPPLIER') ||
      rightNode === 'CANDIDATE';
    if (!isSupplierVisible && isSupplierGap) return null;

    if (!activeEdit) {
      if (!rightNode) return null;
      return <ChevronLeft size={14} color="#9ca3af" style={{ flexShrink: 0, marginTop: '6px' }} />;
    }
    return (
      <button
        type="button"
        onClick={() => handleGapClick(leftNode, rightNode)}
        title="Add company to chain in this position"
        style={{
          width: '20px', height: '20px', borderRadius: '50%',
          border: '1.5px dashed #3b5bfd', background: '#ffffff',
          color: '#3b5bfd', display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
          marginTop: '6px', transition: 'all 0.2s ease',
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = '#3b5bfd';
          e.currentTarget.style.color = '#ffffff';
          e.currentTarget.style.borderStyle = 'solid';
          e.currentTarget.style.transform = 'scale(1.15)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = '#ffffff';
          e.currentTarget.style.color = '#3b5bfd';
          e.currentTarget.style.borderStyle = 'dashed';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <Plus size={11} />
      </button>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Link2 size={12} color="#9ca3af" />
          <span style={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Placement Chain
          </span>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {!activeEdit ? (
            <button
              type="button"
              onClick={() => { setIsEditingChain(true); setFormError(''); fetchCompanies(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 10px',
                       fontSize: '11px', fontWeight: 600, background: '#3b5bfd',
                       color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer',
                       transition: 'all 0.2s ease' }}
            >
              <Pencil size={11} /> Edit
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={isSavingChain}
                onClick={async () => { 
                  setIsSavingChain(true);
                  setFormError(''); 
                  const updated = await fetchChain(); 
                  setIsSavingChain(false);
                  if (onChainSaved) onChainSaved(updated);
                  if (onChainChange) onChainChange(updated);
                  
                  if (entityType.toLowerCase() === 'job' && (hasApplications || hasChainChanges)) {
                    setShowSyncModal(true);
                  } else {
                    setIsEditingChain(false); 
                    setIsAdding(false);
                    setEditingLinkId(null);
                  }
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 10px',
                         fontSize: '11px', fontWeight: 600, background: '#3b5bfd',
                         color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer',
                         transition: 'all 0.2s ease', opacity: isSavingChain ? 0.7 : 1 }}
              >
                <Check size={11} /> {isSavingChain ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => { setIsEditingChain(false); setIsAdding(false); setEditingLinkId(null); setFormError(''); setHasChainChanges(false); fetchChain(); }}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 10px',
                         fontSize: '11px', fontWeight: 600, background: '#f3f4f6',
                         color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                         transition: 'all 0.2s ease' }}
              >
                <X size={13} /> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Pipeline */}
      {loading ? (
        <p style={{ color: '#9ca3af', fontSize: '13px' }}>Loading chain...</p>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: '6px',
                      background: activeEdit ? '#eff3ff' : 'transparent',
                      border: activeEdit ? '1px solid #bfdbfe' : 'none',
                      borderRadius: '10px', padding: activeEdit ? '16px' : '0' }}>
          {activeEdit && <GapButton leftNode={null} rightNode={pipeline[0]} />}
          {pipeline.map((node, idx) => (
            <React.Fragment key={typeof node === 'string' ? node : node[pkName]}>
              {renderNode(node)}
              <GapButton leftNode={node} rightNode={pipeline[idx + 1] || null} />
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Add Link Form */}
      {activeEdit && isAdding && (
        <form onSubmit={handleAdd} style={{ marginTop: '16px', background: '#fff',
                border: '1px solid #e5e7eb', borderRadius: '10px', padding: '18px',
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {/* Company field: read-only label when editing, dropdown when adding */}
          {editingLinkId ? (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                Company
              </label>
              <div style={{
                padding: '8px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb',
                borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#111827',
                display: 'flex', alignItems: 'center', gap: '6px'
              }}>
                <Building2 size={14} color="#6b7280" />
                {editingCompanyName}
                <span style={{ fontSize: '11px', fontWeight: 400, color: '#6b7280', marginLeft: '4px' }}>
                  (Company cannot be changed — delete and re-add to change company)
                </span>
              </div>
            </div>
          ) : (
          <div style={{ gridColumn: '1 / -1', position: 'relative' }} ref={companyDropdownRef}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                Company (Active + Approved) <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowAddCompanyModal(true)}
                style={{
                  background: 'none', border: 'none', color: '#3b5bfd',
                  cursor: 'pointer', fontWeight: 600, fontSize: '12px',
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  whiteSpace: 'nowrap', flexShrink: 0
                }}
              >
                <Building2 size={13} style={{ flexShrink: 0 }} />
                <span>+ Add New Company</span>
              </button>
            </div>

            {/* Custom Searchable Trigger Box */}
            <button
              type="button"
              onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '13px',
                textAlign: 'left',
                backgroundColor: '#fff',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: formData.companyId ? '#111827' : '#9ca3af',
                fontWeight: formData.companyId ? 500 : 400
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedCompany ? (
                  `${selectedCompany.DisplayName || selectedCompany.CompanyName} (${selectedCompany.CompanyCode})`
                ) : (
                  'Select Company'
                )}
              </span>
              <ChevronDown size={16} color="#6b7280" />
            </button>

            {/* Searchable Dropdown Menu Popup */}
            {showCompanyDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                maxHeight: '260px',
                backgroundColor: 'white',
                border: '1px solid #ced4da',
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                zIndex: 1050,
                padding: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                {/* Search Input Box */}
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Search company..."
                    value={companySearchTerm}
                    onChange={(e) => setCompanySearchTerm(e.target.value)}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '6px 10px 6px 28px',
                      fontSize: '12px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  <Search size={14} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
                </div>

                {/* Options List */}
                <div style={{ overflowY: 'auto', maxHeight: '180px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {filteredCompanies.length > 0 ? (
                    filteredCompanies.map(c => {
                      const isSelected = String(formData.companyId) === String(c.CompanyId);
                      return (
                        <div
                          key={c.CompanyId}
                          onClick={() => {
                            setFormData(p => ({
                              ...p,
                              companyId: c.CompanyId,
                              roleLabel: c.IsMSP ? 'MSP' : p.roleLabel
                            }));
                            setShowCompanyDropdown(false);
                            setCompanySearchTerm('');
                          }}
                          style={{
                            padding: '6px 10px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            fontWeight: isSelected ? 'bold' : 'normal',
                            backgroundColor: isSelected ? '#e6f4f1' : 'transparent',
                            color: isSelected ? '#3b5bfd' : '#333'
                          }}
                          onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                          onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          {c.DisplayName || c.CompanyName} ({c.CompanyCode})
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ padding: '8px', fontSize: '12px', color: '#888', textAlign: 'center' }}>
                      No companies found
                    </div>
                  )}

                  {/* Option to Add New Company */}
                  <div
                    onClick={() => {
                      setShowCompanyDropdown(false);
                      setCompanySearchTerm('');
                      setShowAddCompanyModal(true);
                    }}
                    style={{
                      padding: '6px 10px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      color: '#3b5bfd',
                      borderTop: '1px solid #eee',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f4f1'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <Building2 size={12} /> + Add New Company...
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Chain Side: read-only when editing, dropdown when adding */}
          {editingLinkId ? (
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                Chain Side
              </label>
              <div style={{
                padding: '8px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb',
                borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#374151'
              }}>
                {formData.chainSide}
              </div>
            </div>
          ) : (
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                Chain Side *
              </label>
              <select className="edit-input" style={{ width: '100%' }} value={formData.chainSide}
                onChange={e => setFormData(p => ({ ...p, chainSide: e.target.value, isEndClient: false, isW2Employer: false }))}>
                <option value="CLIENT">CLIENT</option>
                {isSupplierVisible && (
                  <option value="SUPPLIER">SUPPLIER</option>
                )}
              </select>
            </div>
          )}

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
              Sequence No *
            </label>
            <input type="number" min="1" className="edit-input" style={{ width: '100%' }} required
              value={formData.sequenceNo}
              onChange={e => setFormData(p => ({ ...p, sequenceNo: parseInt(e.target.value) || 1 }))} />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
              Role Label (optional)
            </label>
            <input type="text" className="edit-input" style={{ width: '100%' }}
              placeholder="e.g. Prime Vendor, Implementation Partner"
              value={formData.roleLabel}
              onChange={e => setFormData(p => ({ ...p, roleLabel: e.target.value }))} />
          </div>

          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            {formData.chainSide === 'CLIENT' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: '#92400e' }}>
                <input type="checkbox" checked={formData.isEndClient}
                  onChange={e => {
                    const checked = e.target.checked;
                    setFormData(p => ({ 
                      ...p, 
                      isEndClient: checked,
                      sequenceNo: checked ? 1 : p.sequenceNo
                    }));
                  }}
                  style={{ accentColor: '#f59e0b', width: '14px', height: '14px' }} />
                Is End Client
              </label>
            )}
            {formData.chainSide === 'SUPPLIER' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: '#1e40af' }}>
                <input type="checkbox" checked={formData.isW2Employer}
                  onChange={e => setFormData(p => ({ ...p, isW2Employer: e.target.checked }))}
                  style={{ accentColor: '#3b82f6', width: '14px', height: '14px' }} />
                Is W2 Employer
              </label>
            )}
          </div>

          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px', alignItems: 'center', marginTop: '4px' }}>
            <button type="submit" disabled={isSubmittingLink} style={{ padding: '7px 18px', background: '#3b5bfd', color: '#fff',
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', opacity: isSubmittingLink ? 0.7 : 1 }}>
            {isSubmittingLink ? (editingLinkId ? 'Updating...' : 'Adding Link...') : (editingLinkId ? 'Update Link' : 'Add Link')}
            </button>
            <button type="button" onClick={() => { setIsAdding(false); setEditingLinkId(null); setEditingCompanyName(''); setFormError(''); }}
              style={{ padding: '7px 18px', background: '#f3f4f6', color: '#374151',
                       border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
              Cancel
            </button>
            {formError && (
              <span style={{ color: '#ef4444', fontSize: '13px', fontWeight: 500 }}>⚠ {formError}</span>
            )}
          </div>
        </form>
      )}

      {/* Add Company Modal for Quick Adding new company directly into chain */}
      {showAddCompanyModal && (
        <AddCompanyModal
          onClose={() => setShowAddCompanyModal(false)}
          onSuccess={handleCompanyCreated}
          companyData={{
            IsClient: formData.chainSide === 'CLIENT',
            IsSupplier: formData.chainSide === 'SUPPLIER'
          }}
        />
      )}

      {/* Sync Chain to Existing Applications Confirmation Modal */}
      {showSyncModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999,
          padding: '16px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            padding: '24px',
            maxWidth: '460px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            textAlign: 'left'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%',
                background: 'rgba(59, 91, 253, 0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0
              }}>
                <Link2 size={22} color="#3b5bfd" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#111827' }}>
                  Update Applications Placement Chain?
                </h3>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  Placement chain updated on Job
                </span>
              </div>
            </div>
            
            <p style={{ fontSize: '13.5px', color: '#374151', lineHeight: '1.55', margin: '0 0 20px 0' }}>
              You updated the client placement chain for this role. Do you want to apply these placement chain changes to <strong>all existing candidate applications</strong> under this job?
            </p>

            {syncFeedback && (
              <div style={{
                marginBottom: '16px',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                background: syncFeedback.startsWith('✓') ? '#ecfdf5' : '#fef2f2',
                color: syncFeedback.startsWith('✓') ? '#3b5bfd' : '#991b1b',
                border: syncFeedback.startsWith('✓') ? '1px solid #a7f3d0' : '1px solid #fecaca'
              }}>
                {syncFeedback}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                disabled={isSyncing}
                onClick={() => {
                  setShowSyncModal(false);
                  setHasChainChanges(false);
                  setIsEditingChain(false);
                  setIsAdding(false);
                  setEditingLinkId(null);
                  setSyncFeedback('');
                }}
                style={{
                  padding: '9px 16px',
                  borderRadius: '7px',
                  border: '1px solid #d1d5db',
                  background: '#f9fafb',
                  color: '#374151',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                No, Only Job
              </button>
              <button
                type="button"
                disabled={isSyncing}
                onClick={async () => {
                  setIsSyncing(true);
                  setSyncFeedback('');
                  try {
                    const token = localStorage.getItem('token');
                    const res = await axios.post(`${BASE_URL}/api/crm/chains/job/${jobId}/sync-to-applications`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.data.success) {
                      setSyncFeedback('✓ Placement chain updated on all applications!');
                      if (onSyncApplications) {
                        onSyncApplications();
                      }
                      setTimeout(() => {
                        setShowSyncModal(false);
                        setHasChainChanges(false);
                        setIsEditingChain(false);
                        setIsAdding(false);
                        setEditingLinkId(null);
                        setSyncFeedback('');
                      }, 1200);
                    } else {
                      setSyncFeedback(`⚠ ${res.data.message || 'Failed to sync applications'}`);
                    }
                  } catch (err) {
                    console.error('Failed to sync chain to applications:', err);
                    setSyncFeedback(`⚠ ${err.response?.data?.message || 'Error syncing applications'}`);
                  } finally {
                    setIsSyncing(false);
                  }
                }}
                style={{
                  padding: '9px 18px',
                  borderRadius: '7px',
                  border: 'none',
                  background: '#3b5bfd',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: isSyncing ? 0.7 : 1
                }}
              >
                {isSyncing ? 'Updating Applications...' : '✓ Yes, Update Applications'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MSP Interactive Picker Modal */}
      {mspPickerData && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1100,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: '#ffffff', borderRadius: '12px', width: '90%', maxWidth: '480px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b', fontWeight: 600 }}>
                  Select Managed Service Provider (MSP)
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                  {mspPickerData.clientName} has multiple active MSP relationships.
                </p>
              </div>
              <button onClick={() => setMspPickerData(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              <p style={{ margin: '0 0 14px 0', fontSize: '13px', color: '#334155' }}>
                Which MSP is this placement going through? Choosing an MSP will auto-insert the MSP at Sequence 1 and {mspPickerData.clientName} at Sequence 2.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {mspPickerData.msps.map(msp => (
                  <div 
                    key={msp.MSPCompanyId}
                    onClick={() => handleSelectMspForChain(msp.MSPCompanyId)}
                    style={{
                      padding: '12px 14px',
                      borderRadius: '8px',
                      border: '1.5px solid #e2e8f0',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      backgroundColor: '#ffffff'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = '#3b5bfd';
                      e.currentTarget.style.backgroundColor = '#eff3ff';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, fontSize: '14px', color: '#0f172a' }}>
                        {msp.MSPName}
                      </span>
                      <span style={{ fontSize: '11px', background: '#ffedd5', color: '#9a3412', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                        MSP ({msp.MSPCode})
                      </span>
                    </div>
                    {msp.Notes && (
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        {msp.Notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ padding: '14px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button 
                type="button" 
                onClick={handleSkipMspForChain} 
                style={{
                  padding: '7px 12px', borderRadius: '6px', border: '1px solid #d1d5db',
                  background: '#ffffff', color: '#475569', fontSize: '12px', cursor: 'pointer'
                }}
              >
                Skip MSP (Direct Client Only)
              </button>
              <button 
                type="button" 
                onClick={() => setMspPickerData(null)} 
                style={{
                  padding: '7px 14px', borderRadius: '6px', border: 'none',
                  background: '#f1f5f9', color: '#475569', fontSize: '12px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlacementChainDisplay;
